import { useCallback, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import PropTypes from "prop-types"
import { capitalize, chain, find, get, keys, map, omitBy, size, split, first } from "lodash"
import { Card, IconButton, Menu, MenuItem, Chip } from "@mui/material"
import { MoreVert } from "@mui/icons-material"
import Table from "components/material/table"
import { PERMISSION_LEVELS, PERMISSION_LEVELS_AS_OPTIONS } from "helpers/permissions"
import { getResourceLabel } from "helpers/permissions/resource-types"
import { PERMISSION, RESOURCE, ROLE } from "helpers/shapes"
import "./style.scss"

export default function Application({ account, application, role, resources, resourceLimits, permissions, update, addNotification, deactivateApplication, openDialog }) {
  const [actionMenuAnchor, setActionMenuAnchor] = useState(null)
  const isAdmin = useMemo(() => role === "admin", [role])

  const getResourceType = useCallback((resourceId) => {
    return chain(resources)
      .find(({ id }) => id === resourceId)
      .get("type")
      .value()
  }, [resources])

  const optionLabels = useMemo(() => chain(resources)
    .keyBy("id")
    .mapValues(getResourceLabel)
    .value(),
  [resources])

  const availableOptionLabels = useMemo(() =>
    omitBy(optionLabels, (_, id) => find(permissions, p => p.id === id)),
  [permissions, optionLabels])

  const addForm = useMemo(() => {
    if (!isAdmin) {
      return null
    }

    function onAdd(data, cb) {
      const query = `mutation ($applicationId: IdOrAlias!, $permissionLevel: PermissionLevel!) {
        ${map(data.resources, (resourceId, index) => `resource${index}: updatePermissions(resourceType: ${getResourceType(resourceId)}, resourceId: "${resourceId}", permissions: { applicationId: $applicationId, permissionLevel: $permissionLevel }) { id }`)}
      }`

      const variables = { permissionLevel: data.permissionLevel, applicationId: application.id }

      update(query, variables, account, {
        reloadData: "applicationPermissions",
      }, (errors, result) => {
        const permissionMessage = get(result, "apiMessages.messages.0.message")
        if (permissionMessage && permissionMessage.match(/limit of \d+ has been reached/i)) {
          addNotification("warning", first(split(permissionMessage, ":")))
        }
        cb(errors, result)
      })
    }

    return {
      title: "Add Permissions",
      submitText: "Add Permissions",
      track: {
        type: "application_permission_added",
        payload: ({ resources, permissionLevel }) => ({
          resources: map(resources, id => optionLabels[id]),
          permissionLevel,
        }),
      },
      onSubmit: onAdd,
      fields:
      [{
        id: "resources",
        label: "Resources",
        type: "multi",
        options: keys(availableOptionLabels),
        optionLabels: availableOptionLabels,
        validations: {
          isRequired: true,
        },
        sort: ([, lbl1], [, lbl2]) => lbl1.localeCompare(lbl2),
      }, {
        id: "permissionLevel",
        label: "Permission Level",
        type: "select",
        options: keys(PERMISSION_LEVELS),
        optionLabels: PERMISSION_LEVELS_AS_OPTIONS,
        validations: {
          isRequired: true,
        },
      }],
    }
  }, [isAdmin, availableOptionLabels, application.id, update, account, getResourceType, addNotification, optionLabels])

  const editForm = useMemo(() => {
    function onEdit(data, cb) {
      const query = `mutation ($applicationId: IdOrAlias!, $resourceId: IdOrShortIdOrAlias!, $resourceType: ResourceType!, $permissionLevel: PermissionLevel!) {
        updatePermissions(resourceType: $resourceType, resourceId: $resourceId, permissions: { applicationId: $applicationId, permissionLevel: $permissionLevel }) { id }
      }`

      const variables = {
        resourceId: data.id,
        resourceType: getResourceType(data.id),
        permissionLevel: data.permissionLevel,
        applicationId: application.id,
      }

      update(query, variables, account, {
        reloadData: ["resourceApplications", "applicationPermissions"],
      }, cb)
    }

    return {
      title: "Edit Permission Level",
      submitText: "Save",
      track: {
        type: "application_permission_level_edited",
        payload: ({ id, permissionLevel }) => ({
          resource: optionLabels[id],
          permissionLevel,
        }),
      },
      isEnabled: () => isAdmin,
      onSubmit: onEdit,
      getInitialValues: ({ permissionLevel }) => ({ permissionLevel }),
      fields: [{
        id: "permissionLevel",
        label: "Permission Level",
        type: "select",
        options: keys(PERMISSION_LEVELS),
        optionLabels: PERMISSION_LEVELS_AS_OPTIONS,
        validations: {
          isRequired: true,
        },
      }],
    }
  }, [account, isAdmin, application, update, getResourceType, optionLabels])

  const deleteForm = useMemo(() => {
    function onDelete(data, cb) {
      const query = `mutation ($applicationId: IdOrAlias!) {
        ${map(data, (resource, index) => `resource${index}: updatePermissions(resourceType: ${resource.type}, resourceId: "${resource.id}", permissions: { applicationId: $applicationId, permissionLevel: NONE }) { id }`)}
      }`

      const variables = { applicationId: application.id }

      update(query, variables, account, {
        reloadData: ["resourceApplications", "applicationPermissions"],
      }, cb)
    }

    return {
      type: "permission",
      track: {
        type: "remove_application_permission",
        payload: ({ id }) => ({ resource: optionLabels[id] }),
      },
      isEnabled: () => isAdmin,
      renderItem: resource => <li key={resource.id}>{resource.name}</li>,
      onSubmit: onDelete,
    }
  }, [account, isAdmin, application, update, optionLabels])

  const handleOpenActionMenu = (event) => setActionMenuAnchor(event.currentTarget)

  const handleCloseActionMenu = () => setActionMenuAnchor(null)

  const navigate = useNavigate()
  const handleDeactivateApplication = () => {
    deactivateApplication(account, application)

    navigate(`/${account}/home/platform`)
  }

  const handleDeactivateApplicationClick = () => {
    let confirmationText = `Are you sure you want to de-authorize ${application.name}?`
    if (size(permissions)) {
      confirmationText += " This will remove all permissions that were assigned to it."
    }

    handleCloseActionMenu()

    openDialog({
      title: `De-authorize ${application.name} application`,
      contentText: confirmationText,
      buttons: [{
        text: "De-authorize",
        type: "danger",
        action: handleDeactivateApplication,
      }],
    })
  }

  return <div className="modules_application">
    {application && <>
      <Card className="application-header" square>
        <div className="info">
          <div className="name">{application.name}</div>
          <div className="description">{application.description}</div>
          <div className="resource-limits">
            <Chip color="primary" label={`Portfolios shared: ${resourceLimits.portfolios.shared} / ${resourceLimits.portfolios.limit}`} />
            <Chip color="primary" label={`Baskets shared: ${resourceLimits.baskets.shared} / ${resourceLimits.baskets.limit}`} />
          </div>
        </div>
        <div className="actions">
          <IconButton onClick={handleOpenActionMenu} size="large">
            <MoreVert />
          </IconButton>
        </div>
      </Card>
      <Card className="resources" square>
        <Table
          title="Resources Permissions"
          idField="id"
          addForm={addForm}
          editForm={editForm}
          deleteForm={deleteForm}
          data={permissions || []}
          columns={[{
            name: "Name",
            id: "name",
          }, {
            name: "Type",
            id: "type",
            formatter: capitalize,
          }, {
            name: "Description",
            id: "description",
          }, {
            name: "Permission Level",
            id: "permissionLevel",
            formatter: capitalize,
          }]}
        />
      </Card>

      <Menu
        anchorEl={actionMenuAnchor}
        keepMounted
        open={Boolean(actionMenuAnchor)}
        onClose={handleCloseActionMenu}
      >
        <MenuItem onClick={handleDeactivateApplicationClick}>De-authorize</MenuItem>
      </Menu>
    </>}
  </div>
}

Application.propTypes = {
  account: PropTypes.string.isRequired,
  application: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    alias: PropTypes.string.isRequired,
    description: PropTypes.string,
    logoFilename: PropTypes.string,
  }).isRequired,
  resourceLimits: PropTypes.shape({
    portfolios: PropTypes.shape({
      shared: PropTypes.number.isRequired,
      limit: PropTypes.number.isRequired,
    }),
    baskets: PropTypes.shape({
      shared: PropTypes.number.isRequired,
      limit: PropTypes.number.isRequired,
    }),
  }),
  role: ROLE.isRequired,
  resources: PropTypes.arrayOf(RESOURCE).isRequired,
  update: PropTypes.func.isRequired,
  addNotification: PropTypes.func.isRequired,
  permissions: PropTypes.arrayOf(PERMISSION),
  deactivateApplication: PropTypes.func.isRequired,
  openDialog: PropTypes.func.isRequired,
}
